import { ListItemButton, Box, ListItemText, Collapse, List } from "@mui/material";
import React, { useState } from "react";
import { BRAND_GREEN, SURFACE_MINT } from "../../../components/shared/colors";

export interface MenuItem {
    title: string;
    items?: MenuItem[];
}

export const houseProjectionMenu: MenuItem[] = [
    {
        title: "Proposals",
        items: [
            {
                title: "Overview"
            },
            {
                title: "2025",
                items: [{ title: "ARC-PLC Payments" }]
            },
            {
                title: "2024",
                items: [{ title: "EQIP Projection" }]
            }
        ]
    }
];

export function MenuItem({
    item,
    index,
    selectedItem,
    onMenuSelect,
    level
}: {
    item: MenuItem;
    index: string;
    selectedItem: string;
    onMenuSelect: (i: string) => void;
    level: number;
}): JSX.Element {
    const isTop2024Proposals = level === 0 && item.title === "2024 Proposals";
    const isHouseAgCommittee = level === 0 && item.title === "House Ag Committee";

    const [isOpen, setIsOpen] = useState(true);

    const isSelected = selectedItem === index;
    const hasSelectedChild = selectedItem.startsWith(`${index}-`);

    const handleClick = () => {
        if (isHouseAgCommittee) {
            setIsOpen(!isOpen);
            return;
        }
        if (item.items && item.items.length > 0) {
            setIsOpen(true);
            onMenuSelect(`${index}-0`);
            return;
        }
        if (!isTop2024Proposals) {
            onMenuSelect(index);
        }
    };

    const getTextColor = () => {
        if (isSelected || hasSelectedChild) return BRAND_GREEN;
        return "#666666";
    };

    const getBackgroundColor = () => {
        if (level > 0) return "#F5F7F6";
        return SURFACE_MINT;
    };

    const getHoverBackgroundColor = () => {
        if (isTop2024Proposals) return "inherit";
        if (level > 0) return "rgba(0, 0, 0, 0.08)";
        return "rgba(0, 0, 0, 0.04)";
    };

    const showBorder = level > 0;

    return (
        <>
            <ListItemButton
                onClick={handleClick}
                disabled={isTop2024Proposals}
                sx={{
                    "my": 0,
                    "py": level === 0 ? 2 : 1.5,
                    "pl": level * 3,
                    "color": getTextColor(),
                    "backgroundColor": getBackgroundColor(),
                    "borderBottom": "1px solid #ccd7d1",
                    "&:hover": {
                        backgroundColor: getHoverBackgroundColor()
                    },
                    "&.Mui-disabled": {
                        opacity: 1,
                        color: "#666666"
                    },
                    "cursor": isTop2024Proposals ? "default" : "pointer"
                }}
            >
                <ListItemText
                    primary={
                        <Box
                            sx={{
                                mx: 3,
                                fontFamily: '"Roboto", sans-serif',
                                fontWeight: level === 0 ? 600 : 500,
                                fontSize: level === 0 ? "inherit" : "0.95em",
                                borderLeft: showBorder
                                    ? `4px solid ${isSelected || hasSelectedChild ? BRAND_GREEN : "#ccd7d1"}`
                                    : "none",
                                paddingLeft: showBorder ? 2 : 0,
                                color: "inherit"
                            }}
                        >
                            {item.title}
                        </Box>
                    }
                />
            </ListItemButton>
            {item.items && item.items.length > 0 && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {item.items.map(
                            (subItem, subIndex) =>
                                subItem && (
                                    <MenuItem
                                        key={subItem.title || `sub-item-${subIndex}`}
                                        item={subItem}
                                        index={`${index}-${subIndex}`}
                                        selectedItem={selectedItem}
                                        onMenuSelect={onMenuSelect}
                                        level={level + 1}
                                    />
                                )
                        )}
                    </List>
                </Collapse>
            )}
        </>
    );
}
